import { useEffect } from "react";
import { motion } from "framer-motion";
import { PenTool, Users, FolderLock, Briefcase, ArrowRight, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import Testimonials from "@/components/Testimonials";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { seoConfig } from "@/data/seoConfig";

const products = [
  {
    id: "signulu",
    icon: PenTool,
    logo: "/assets/logo-signulu.png",
    visual: "/assets/banner-signulu.jpg",
    name: "Signulu",
    tagline: "A cloud-based eSignature software solution.",
    desc: "Signulu is a cloud-based eSignature software platform built for the utmost convenience of individuals, professionals, and businesses. It's a feature-rich solution that's advanced, secure, legally binding, and offers end-to-end data encryption — accessible from any device, anywhere, at any time.",
    primaryCta: { label: "Get a Free 14-Day Trial", href: "https://app.signulu.com/account/register" },
    exploreHref: "https://signulu.signuluone.com/",
  },
  {
    id: "quikhyr",
    icon: Users,
    logo: "/assets/logo-quikhyr.svg",
    visual: null,
    name: "QuikHyr AI",
    tagline: "AI-driven recruitment, simplified.",
    desc: "QuikHyr unifies intelligent data analysis, automated workflows, and actionable insights into a single, secure cloud-native platform. Advanced AI/ML capabilities enable precise candidate matching, while enterprise-grade security ensures end-to-end encryption, access controls, and compliance-ready operations.",
    primaryCta: { label: "See QuikHyr in Action", href: "https://demo.quikhyr.ai/" },
    exploreHref: "https://quikhyr.ai/",
  },
  {
    id: "dms",
    icon: FolderLock,
    logo: "/assets/logo-dms.webp",
    visual: null,
    name: "DMS",
    tagline: "Intelligent Secure Document Management for Modern Enterprises.",
    desc: "Our Document Management System helps businesses centralize and manage documents efficiently — from capture and secure storage to workflow automation and audit compliance. Built for scale, it stays compliant with DPDP, IT Act, and GDPR, and integrates seamlessly with CRMs, ERPs, eSign, eKYC, and eStamp systems.",
    primaryCta: { label: "Schedule a Free Demo", href: "https://calendly.com/signup-xtod/signulu-demo-1" },
    exploreHref: "https://dms.signuluone.com/",
  },
  {
    id: "attainica",
    icon: Briefcase,
    logo: "/assets/logo-attainica.png",
    visual: "/assets/banner-attainica.jpg",
    name: "Attainica",
    tagline: "A full-featured HR ERP system.",
    desc: "Attainica is an efficacious staffing ERP system — a one-stop destination for all your staffing needs. It automates applicant tracking, uses AI/ML to match the right resume to your job requirements, runs built-in email campaigns, and enables intuitive onboarding across your business operations.",
    primaryCta: { label: "Schedule a Free Demo", href: "https://www.attainica.com/index.php/requestfreedemo" },
    exploreHref: "https://www.attainica.com/",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const OurProducts = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO {...seoConfig.ourProducts} jsonLd={[buildBreadcrumbJsonLd(seoConfig.ourProducts.crumbs)]} />
      <PageHero
        title="Our Products"
        crumbs={[{ label: "Home", href: "/" }, { label: "Our Products" }]}
        lightfall
      />

      <section data-testid="products-intro" className="pt-24 lg:pt-16 pb-16 bg-white">
        <motion.div {...fadeUp} className="max-w-3xl mx-auto px-6 text-center">
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
            Millennium Infotech
          </span>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
            Pioneering Innovation and Modern Business Solutions
          </h2>
          <div className="mt-5 mx-auto h-0.5 w-12 bg-miti-blue" />
          <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
            With more than 20 years of experience enabling enterprises to become more productive and functional,
            Millennium Infotech is a comprehensive partner for building applications, products, and solutions. We
            foster innovation, deliver technology, and craft contemporary business solutions — including four
            exceptional products that empower organizations.
          </p>
        </motion.div>

        <motion.nav
          {...fadeUp}
          className="mt-10 flex flex-wrap items-center justify-center gap-3 px-6"
          data-testid="products-quicknav"
        >
          {products.map((p) => (
            <a
              key={p.id}
              href={`#${p.id}`}
              data-testid={`products-quicknav-${p.id}`}
              className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 px-5 py-2 text-sm font-medium text-slate-700 hover:border-miti-blue hover:text-miti-blue transition-colors"
            >
              <img src={p.logo} alt="" className="h-5 max-w-[90px] object-contain" />
              {p.name}
            </a>
          ))}
        </motion.nav>
      </section>

      {products.map((p, i) => (
        <section
          key={p.id}
          id={p.id}
          data-testid={`product-section-${p.id}`}
          className={`py-24 lg:py-16 ${i % 2 ? "bg-slate-50/70" : "bg-white"}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <motion.div {...fadeUp} className={i % 2 ? "lg:order-2" : ""}>
              <div className="mt-6 h-10 flex items-center">
                <img src={p.logo} alt={`${p.name} logo`} className="max-h-10 max-w-[200px] object-contain" />
              </div>
              <h2 className="mt-5 font-heading font-bold tracking-tight text-2xl sm:text-3xl text-slate-900 leading-snug">
                {p.tagline}
              </h2>
              <p className="mt-5 text-base md:text-lg text-slate-500 leading-relaxed">{p.desc}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href={p.primaryCta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`product-cta-${p.id}`}
                  className="group inline-flex items-center gap-2.5 rounded-lg bg-miti-blue px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors"
                >
                  {p.primaryCta.label}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <a
                  href={p.exploreHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`product-explore-${p.id}`}
                  className="group inline-flex items-center gap-2 rounded-lg border border-slate-300 px-6 py-3.5 text-sm font-semibold text-slate-800 hover:border-miti-blue hover:text-miti-blue transition-colors"
                >
                  Explore {p.name}
                  <ArrowUpRight
                    size={15}
                    className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-70px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className={i % 2 ? "lg:order-1" : ""}
            >
              {p.visual ? (
                <div className="rounded-3xl overflow-hidden border-[3px] border-miti-blue shadow-xl shadow-miti-blue/10">
                  <img src={p.visual} alt={p.name} className="w-full h-[320px] lg:h-[380px] object-cover" />
                </div>
              ) : (
                <div className="rounded-3xl border border-slate-200 shadow-2xl shadow-slate-900/10 bg-gradient-to-br from-miti-blue/5 to-sky-400/10 h-[320px] lg:h-[380px] flex items-center justify-center p-12">
                  <img src={p.logo} alt={p.name} className="max-h-20 max-w-[70%] object-contain" />
                </div>
              )}
            </motion.div>
          </div>
        </section>
      ))}

      <CTABand
        layout="centered"
        eyebrow="Unlock Potential with Data, Digital Transformation, Gen AI, and Engineering"
        title="Transform Your Business with Expert Technology and AI Solutions"
        description="Engage with our technology experts to unlock the full potential of your business. Our team excels in Generative AI, Data, Digital Transformation, and Product Engineering, providing comprehensive solutions tailored to your needs."
        ctaLabel="Contact Us"
        ctaHref="/contact"
      />
      <Testimonials />
    </>
  );
};

export default OurProducts;
